<script setup>
import { ref, computed, provide } from 'vue';
import { Virtualizer } from 'virtua/vue';
import { useBreakpoints } from '@vueuse/core';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import ConversationItem from './ConversationItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';

import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversationList: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  showEndOfListMessage: { type: Boolean, default: false },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore', 'refresh']);

const conversationListRef = ref(null);
const virtualListRef = ref(null);
const isContextMenuOpen = ref(false);

provide('contextMenuElementTarget', virtualListRef);

const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showExpandedCards = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);

useChatListKeyboardEvents(conversationListRef);

const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

const onContextMenuToggle = state => {
  isContextMenuOpen.value = state;
};

const loadMoreConversations = () => {
  emit('loadMore');
};

const refreshingState = ref('none'); // none, pulling, refreshing
const startY = ref(0);
const currentY = ref(0);

const pullDistance = computed(() => {
  const dist = currentY.value - startY.value;
  // apply a friction factor so it doesn't pull too far easily
  return dist > 0 ? Math.min(dist * 0.4, 80) : 0;
});

const onTouchStart = (e) => {
  // Only enable pull-to-refresh if we are at the very top of the list
  if (conversationListRef.value && conversationListRef.value.scrollTop <= 0) {
    startY.value = e.touches[0].clientY;
    currentY.value = e.touches[0].clientY;
    refreshingState.value = 'pulling';
  }
};

const onTouchMove = (e) => {
  if (refreshingState.value === 'pulling') {
    currentY.value = e.touches[0].clientY;
    // If pulling down, prevent default to stop the whole page from scrolling or reloading
    if (currentY.value > startY.value) {
      // We can't actually preventDefault on passive listeners easily in Vue without passive modifier,
      // but overscroll-behavior-y: contain handles the browser default refresh anyway.
    }
  }
};

const onTouchEnd = () => {
  if (refreshingState.value === 'pulling') {
    if (pullDistance.value >= 60) {
      refreshingState.value = 'refreshing';
      emit('refresh');
      // Reset the visual state after a short delay so user sees it completed
      setTimeout(() => {
        refreshingState.value = 'none';
        startY.value = 0;
        currentY.value = 0;
      }, 1000);
    } else {
      refreshingState.value = 'none';
      startY.value = 0;
      currentY.value = 0;
    }
  }
};

provide('toggleContextMenu', onContextMenuToggle);

defineExpose({ conversationListRef });
</script>

<template>
  <div
    ref="conversationListRef"
    class="flex-1 min-h-0 overflow-y-auto conversations-list relative"
    style="overscroll-behavior-y: contain;"
    :class="{ '!overflow-hidden': isContextMenuOpen }"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <!-- Pull to refresh indicator -->
    <div 
      class="flex justify-center items-center w-full overflow-hidden transition-all duration-200"
      :style="{ height: refreshingState !== 'none' ? pullDistance + 'px' : '0px', minHeight: refreshingState === 'refreshing' ? '60px' : '0px' }"
    >
      <div 
        class="flex justify-center items-center rounded-full bg-[#e7f702]/20 text-[#e7f702] border border-[#e7f702]/30 shadow-sm"
        :class="refreshingState === 'refreshing' ? 'animate-spin w-8 h-8' : 'w-8 h-8'"
        :style="{ transform: refreshingState === 'pulling' ? `rotate(${pullDistance * 4}deg)` : 'none', opacity: pullDistance / 60 }"
      >
        <span class="i-lucide-refresh-cw w-4 h-4"></span>
      </div>
    </div>
    <Virtualizer
      ref="virtualListRef"
      v-slot="{ item }"
      :data="conversationList"
      class="[&>div:has(+_div_.active)>*]:!border-n-surface-1 [&>div:has(+_div_.selected)>*]:!border-n-surface-1"
    >
      <ConversationItem
        :key="item.id + '-' + (item.unread_count || 0) + '-' + item.last_activity_at"
        :source="item"
        :label="label"
        :team-id="teamId"
        :folders-id="foldersId"
        :conversation-type="conversationType"
        :show-assignee="showAssignee"
        :show-expanded="showExpandedCards"
      />
    </Virtualizer>
    <!-- Startup loading spinner removed per user request -->
    <div v-if="isLoading" class="flex justify-center my-4 hidden">
    </div>
    <p v-else-if="showEndOfListMessage" class="p-4 text-center w-full block mx-auto text-n-slate-11">
      {{ $t('CHAT_LIST.EOF') }}
    </p>
    <IntersectionObserver
      v-else
      :options="intersectionObserverOptions"
      @observed="loadMoreConversations"
    />
  </div>
</template>
